import React, { useMemo, useCallback } from 'react';
import {
  mergeClasses,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { ArrowLeft24Regular } from '@fluentui/react-icons';
import {
  getIsFirstStep,
  getIsLastStep,
  getCurrentStepId,
  getIsStepVisited,
  useWizardService,
} from '@bingads-webui-react/wizard-core';

import {
  NextButton,
  BackButton,
  SaveAndExitButton,
  BackToSummaryButton,
  CancelButton,
} from './buttons';
import { withErrorAbove } from './with-error-above';

const useBasicStepFooterStyles = makeStyles({
  footer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.padding('16px'),
    ...shorthands.gap('8px'),
    '& button': {
      minWidth: 'fit-content',
    },
  },
});

interface BasicStepFooterProps {
  lastStepNextButtonText?: string;
  nextButtonText?: string;
  nextDisabled?: boolean;
  backButtonText?: string;
  backButtonAppearance?: 'primary' | 'secondary' | 'outline' | 'subtle' | 'transparent';
  backButtonIcon?: any;
  summaryStepId?: string;
  backToSummaryButtonText?: string;
  cancelButtonText?: string;
  useCancelButton?: boolean;
  hideBackButton?: boolean;
  classes?: {
    root?: string;
  };
}

export const BasicStepFooter: React.FC<BasicStepFooterProps> = withErrorAbove((props) => {
  const {
    lastStepNextButtonText,
    nextButtonText,
    nextDisabled,
    backButtonText,
    backButtonAppearance,
    backButtonIcon,
    summaryStepId,
    backToSummaryButtonText,
    cancelButtonText,
    useCancelButton,
    hideBackButton,
  } = props;
  const classes = useBasicStepFooterStyles();
  const { state } = useWizardService();
  const isFirstStep = getIsFirstStep(state);
  const isLastStep = getIsLastStep(state);
  const currentStepId = getCurrentStepId(state);
  const isSummaryStepVisited = getIsStepVisited(state, summaryStepId);

  const renderBackButton = useCallback(() => {
    if (!isFirstStep && !hideBackButton) {
      return <BackButton buttonText={backButtonText} appearance={backButtonAppearance} icon={backButtonIcon} />;
    }
    return null;
  }, [backButtonAppearance, backButtonIcon, backButtonText, hideBackButton, isFirstStep]);

  const renderNextButton = useCallback((appearance) => {
    if (isLastStep) {
      return <SaveAndExitButton buttonText={lastStepNextButtonText} disabled={nextDisabled} appearance={appearance} />;
    }
    return <NextButton buttonText={nextButtonText} disabled={nextDisabled} appearance={appearance} />;
  }, [isLastStep, lastStepNextButtonText, nextButtonText, nextDisabled]);

  const navigationActions = useMemo(() => {
    if (summaryStepId && isSummaryStepVisited && currentStepId !== summaryStepId) {
      return (
        <>
          {renderBackButton()}
          <BackToSummaryButton buttonText={backToSummaryButtonText} summaryStepId={summaryStepId} />
          {renderNextButton('secondary')}
        </>
      );
    }
    return (
      <>
        {renderBackButton()}
        {renderNextButton('primary')}
      </>
    );
  }, [backToSummaryButtonText, currentStepId, isSummaryStepVisited, renderBackButton, renderNextButton, summaryStepId]);

  return (
    <div className={mergeClasses('basic-step-footer', classes.footer, props.classes?.root)}>
      {navigationActions}
      {useCancelButton && <CancelButton buttonText={cancelButtonText} />}
    </div>
  );
});

const useCardFooterStyles = makeStyles({
  root: {
    ...shorthands.border('none'),
  },
});

export const CardFooter: React.FC<BasicStepFooterProps> = (props) => {
  const classes = useCardFooterStyles();

  return (
    <BasicStepFooter
      classes={classes}
      backButtonAppearance="transparent"
      backButtonIcon={<ArrowLeft24Regular />}
      {...props}
    />
  );
};
